import React from 'react';

interface PlanChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isProcessing: boolean;
    changeType: 'upgrade' | 'downgrade';
    currentPlan: string;
    newPlan: string;
    planDetails: {
        effectiveDate: string;
        amount: number;
        nextBillingDate: string;
        newPlanPrice?: string;
    };
}

const PlanChangeModal: React.FC<PlanChangeModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isProcessing,
    changeType,
    currentPlan,
    newPlan,
    planDetails
}) => {
    if (!isOpen) return null;

    const isUpgrade = changeType === 'upgrade';
    const actionVerb = isUpgrade ? 'upgrade' : 'downgrade';
    const actionNoun = isUpgrade ? 'Upgrade' : 'Downgrade';
    const effectiveText = isUpgrade ? 'Immediately' : new Date(planDetails.effectiveDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const primaryColor = isUpgrade ? '#2196f3' : '#ff9800';
    const gradientColor = isUpgrade ? '#21cbf3' : '#ffb74d';

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
            
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: 12,
                padding: 32,
                maxWidth: 600,
                width: '90%',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                border: '1px solid #e1e5e9'
            }}>
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{
                        margin: '0 0 8px 0',
                        color: '#1a1a1a',
                        fontSize: '1.5rem',
                        fontWeight: '600'
                    }}>
                        Confirm Plan {actionNoun}
                    </h3>
                    <div style={{
                        height: 3,
                        background: `linear-gradient(90deg, ${primaryColor}, ${gradientColor})`,
                        borderRadius: 2,
                        marginBottom: 16
                    }} />
                </div>

                <div style={{ marginBottom: 24, color: '#444', lineHeight: 1.6 }}>
                    <p style={{ margin: '0 0 16px 0', fontSize: '1.1rem' }}>
                        You are about to {actionVerb} from <strong>{currentPlan}</strong> to <strong>{newPlan}</strong>.
                    </p>

                    <div style={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                        padding: 20,
                        marginBottom: 16
                    }}>
                        <h4 style={{ margin: '0 0 12px 0', color: '#495057', fontSize: '1rem' }}>
                            📋 {actionNoun} Details
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Effective Date:</span>
                                <strong>{effectiveText}</strong>
                            </div>

                            {isUpgrade && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Immediate Charge:</span>
                                        <strong style={{ color: primaryColor }}>
                                            ${planDetails.amount.toFixed(2)}
                                        </strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Next Billing Date:</span>
                                        <strong>
                                            {new Date(planDetails.nextBillingDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </strong>
                                    </div>
                                    {planDetails.newPlanPrice && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Followed by:</span>
                                            <strong style={{ color: primaryColor }}>
                                                {planDetails.newPlanPrice}
                                            </strong>
                                        </div>
                                    )}
                                </>
                            )}

                            {!isUpgrade && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>New Plan Price:</span>
                                    <strong style={{ color: primaryColor }}>
                                        {planDetails.newPlanPrice || `$${planDetails.amount.toFixed(2)}/month`}
                                    </strong>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: isUpgrade ? '#e3f2fd' : '#fff3e0',
                        border: `1px solid ${isUpgrade ? '#bbdefb' : '#ffcc02'}`,
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 16
                    }}>
                        <p style={{ margin: 0, fontSize: '0.95rem', color: isUpgrade ? '#1565c0' : '#e65100' }}>
                            <strong>💳 Billing Information:</strong><br />
                            {isUpgrade ? (
                                <>
                                    The plan change is immediate and you will be charged the prorated amount of <strong>${planDetails.amount.toFixed(2)}</strong> today.
                                    Your new billing cycle will continue from {new Date(planDetails.nextBillingDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.
                                </>
                            ) : (
                                <>
                                    The downgrade will take effect on {effectiveText}. You'll continue to have access to your current features until then.
                                    No charges will be made today.
                                </>
                            )}
                        </p>
                    </div>

                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                        You can {isUpgrade ? 'downgrade or cancel' : 'upgrade or cancel'} your plan at any time from your account settings.
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            border: '1px solid #dee2e6',
                            borderRadius: 8,
                            fontSize: '1rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: '100px'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#e9ecef';
                            e.currentTarget.style.borderColor = '#adb5bd';
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                        onMouseDown={(e) => {
                            e.currentTarget.style.transform = 'scale(0.98)';
                        }}
                        onMouseUp={(e) => {
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        disabled={isProcessing}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: primaryColor,
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: `0 3px 6px rgba(${isUpgrade ? '33, 150, 243' : '255, 152, 0'}, 0.3)`,
                            minWidth: '120px',
                            opacity: isProcessing ? 0.7 : 1
                        }}
                        onMouseOver={(e) => {
                            if (!isProcessing) {
                                e.currentTarget.style.backgroundColor = isUpgrade ? '#1976d2' : '#f57c00';
                                e.currentTarget.style.boxShadow = `0 4px 8px rgba(${isUpgrade ? '33, 150, 243' : '255, 152, 0'}, 0.4)`;
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isProcessing) {
                                e.currentTarget.style.backgroundColor = primaryColor;
                                e.currentTarget.style.boxShadow = `0 3px 6px rgba(${isUpgrade ? '33, 150, 243' : '255, 152, 0'}, 0.3)`;
                                e.currentTarget.style.transform = 'scale(1)';
                            }
                        }}
                        onMouseDown={(e) => {
                            if (!isProcessing) {
                                e.currentTarget.style.transform = 'scale(0.98)';
                            }
                        }}
                        onMouseUp={(e) => {
                            if (!isProcessing) {
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }
                        }}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                    width: 16,
                                    height: 16,
                                    border: '2px solid transparent',
                                    borderTop: '2px solid white',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }} />
                                Processing...
                            </div>
                        ) : (
                            `Confirm ${actionNoun}`
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PlanChangeModal;